import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Checkbox from '@mui/material/Checkbox';
import {
  Avatar,
  Box,
  Typography,
  Button,
  Select,
  MenuItem,
  IconButton,
  Divider,
  FormControl,
  Popover,
  Slider,
  Autocomplete,
  TextField,
  InputAdornment,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import './InstructorTable.css';
import { formatText } from '../../../../utils/textUtils';
import { BiSort } from 'react-icons/bi';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import OneOnOneSessionIcon from '../../../../assets/Admin/SessionIcons/one-on-one-session-icon.svg';
import GroupSessionIcon from '../../../../assets/Admin/SessionIcons/group-session-icon.svg';
import PrimeSessionIcon from '../../../../assets/Admin/SessionIcons/prime-session-icon.svg';
import ReactCountryFlag from 'react-country-flag';
import { getCodeList } from 'country-list';

const typeColors = {
  Academic: { color: '#6B60E7', bg: '#F0EFFF' },
  Creative: { color: '#14B334', bg: '#F2FFF5' },
};

const StatusColor = {
  enable: { color: '#14B334', bgcolor: '#F2FFF5', value: 'Enabled' },
  disable: { color: '#949DA6', bgcolor: '#F5F5F5', value: 'Disabled' },
};

// PopoverSubjects component for subjects popover
const PopoverSubjects = ({ subjects, rowId }) => {
  const [anchorEl, setAnchorEl] = useState(null);

  // Normalize to titles and deduplicate while preserving order
  const subjectTitlesAll = (subjects || []).map(s =>
    typeof s === 'object' && s?.title ? s.title : s
  );
  const seen = new Set();
  const uniqueTitles = subjectTitlesAll.filter(title => {
    if (!title) return false;
    const key = String(title).trim().toLowerCase();
    if (seen.has(key)) return false;
    seen.add(key);
    return true;
  });

  const open = Boolean(anchorEl) && uniqueTitles.length > 1;

  const handlePopoverOpen = event => {
    if (uniqueTitles.length > 1) setAnchorEl(event.currentTarget);
  };

  const handlePopoverClose = () => setAnchorEl(null);

  return (
    <div
      onMouseEnter={handlePopoverOpen}
      onMouseLeave={handlePopoverClose}
      className="instructor-subjects-container"
    >
      <Box className="flex items-center" sx={{ gap: '6px' }}>
        <Typography className="subject-text">
          {formatText(uniqueTitles && uniqueTitles.length > 0 ? uniqueTitles[0] : '---')}
        </Typography>
        {uniqueTitles.length > 1 && (
          <Box className="request-subject-count-badge">
            <Typography className="request-subject-count-text">
              +{uniqueTitles.length - 1}
            </Typography>
          </Box>
        )}
      </Box>

      {uniqueTitles.length > 1 && (
        <Popover
          id={`mouse-over-popover-${rowId}`}
          sx={{
            pointerEvents: 'none',
            '& .MuiPopover-paper': {
              overflow: 'visible',
              borderRadius: '8px',
              boxShadow: '0px 4px 15px rgba(0, 0, 0, 0.1)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: '-6px',
                left: '20px',
                width: '12px',
                height: '12px',
                backgroundColor: 'white',
                transform: 'rotate(45deg)',
                borderLeft: '1px solid rgba(0, 0, 0, 0.08)',
                borderTop: '1px solid rgba(0, 0, 0, 0.08)',
              },
            },
          }}
          open={open}
          anchorEl={anchorEl}
          anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
          transformOrigin={{ vertical: 'top', horizontal: 'left' }}
          onClose={handlePopoverClose}
          disableRestoreFocus
          PaperProps={{
            onMouseEnter: handlePopoverOpen,
            onMouseLeave: handlePopoverClose,
            sx: { pointerEvents: 'auto', width: '250px' },
          }}
        >
          <Box
            sx={{
              p: 1,
              '& > :not(:last-child)': { borderBottom: '1px solid rgba(0, 0, 0, 0.08)' },
            }}
          >
            {uniqueTitles.map((subject, index) => (
              <Typography
                key={index}
                sx={{
                  py: 1,
                  px: 2,
                  fontSize: '12px',
                  fontFamily: 'Poppins',
                  '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)', borderRadius: '4px' },
                }}
              >
                {formatText(subject)}
              </Typography>
            ))}
          </Box>
        </Popover>
      )}
    </div>
  );
};

const countryListObject = getCodeList();
const CountriesList = Object.entries(countryListObject).map(([code, name]) => ({
  code,
  name,
}));

// Country name mapping for common variations
const countryNameMapping = {
  iran: 'iran, islamic republic of',
  russia: 'russian federation',
  'south korea': 'korea, republic of',
  'north korea': "korea, democratic people's republic of",
  usa: 'united states',
  'united states': 'united states',
  uk: 'united kingdom',
  britain: 'united kingdom',
  england: 'united kingdom',
  uae: 'united arab emirates',
  emirates: 'united arab emirates',
  vietnam: 'viet nam',
  bolivia: 'bolivia, plurinational state of',
  venezuela: 'venezuela, bolivarian republic of',
  tanzania: 'tanzania, united republic of',
  macedonia: 'macedonia, the former yugoslav republic of',
  moldova: 'moldova, republic of',
  syria: 'syrian arab republic',
  laos: "lao people's democratic republic",
  brunei: 'brunei darussalam',
  congo: 'congo, the democratic republic of the',
  'ivory coast': "côte d'ivoire",
  'czech republic': 'czechia',
  'east timor': 'timor-leste',
  swaziland: 'eswatini',
  myanmar: 'myanmar',
  burma: 'myanmar',
};

const InstructorTable = ({
  instructorList = [],
  selectedIds = new Set(),
  onSelectOne = () => {},
  onSelectAll = () => {},
  onRemoveSelected = () => {},
  // Filter props
  instructorTypeFilter = 'All',
  countryFilter = 'All',
  genderFilter = 'All',
  ratingFilter = 'All',
  searchQuery = '',
  onFilterChange = () => {},
  org_id,
  onTotalSessionsSort = () => {},
  onRatingSort = () => {},
  totalCount = 0,
}) => {
  const navigate = useNavigate();

  const allSelected = instructorList.length > 0 && instructorList.every(i => selectedIds.has(i.id));
  const someSelected = instructorList.some(i => selectedIds.has(i.id)) && !allSelected;

  // Handle cell click to navigate to instructor detail
  const handleCellClick = id => {
    navigate(`/admin/users/instructor-details/${id}?org_id=${org_id}`);
  };

  // Strictly rely on API results; do not apply client-side filtering that could
  // conflict with backend logic and cause mismatches with totalCount.
  const displayData = useMemo(() => {
    // Dependencies include filters/search so the table re-renders on their change,
    // but we intentionally do not filter locally.
    return instructorList;
  }, [instructorList, instructorTypeFilter, countryFilter, genderFilter, searchQuery]);

  const renderCountry = countryName => {
    if (!countryName) return '---';

    // First try exact match
    let countryMatch = CountriesList.find(
      country => country.name.toLowerCase() === countryName.toLowerCase()
    );

    // If no exact match, try with mapping
    if (!countryMatch) {
      const mappedName = countryNameMapping[countryName.toLowerCase()];
      if (mappedName) {
        countryMatch = CountriesList.find(
          country => country.name.toLowerCase() === mappedName.toLowerCase()
        );
      }
    }

    return countryMatch ? (
      <Box className="instructor-country-flag-container">
        <ReactCountryFlag
          countryCode={countryMatch.code}
          svg
          className="instructor-country-flag-style"
        />
        <Typography className="instructor-country-text-style">{countryName}</Typography>
      </Box>
    ) : (
      <Box className="instructor-country-flag-container">
        <Typography className="instructor-country-text-style">{countryName}</Typography>
      </Box>
    );
  };

  return (
    <Box className="instructor-table-scroll-container">
      <Box className="instructor-table-wrapper">
        <table className="instructor-table" style={{ width: '100%' }}>
          <thead>
            <tr>
              <th>Name</th>
              <th>Instructor Type</th>
              <th>Country</th>
              <th>Subjects</th>
              <th>Type of session</th>
              <th>Total Sessions</th>
              <th>Rating</th>
            </tr>
          </thead>
          <tbody>
            {displayData.length === 0 ? (
              <tr>
                <td colSpan={7} align="center" className="org-instructor-no-data-cell">
                  No data available
                </td>
              </tr>
            ) : (
              displayData.map(row => (
                <tr
                  key={row.id}
                  className="instructor-table-row"
                  onClick={() => handleCellClick(row.id)}
                >
                  <td>
                    <Box className="instructor-avatar-name-container">
                      <Avatar src={row.profile_image} sx={{ width: 32, height: 32, mr: 1 }} />
                      <Typography className="instructor-name-text-style">
                        {row.first_name} {row.last_name}
                      </Typography>
                    </Box>
                  </td>
                  <td>
                    <span
                      className="instructor-type-badge-style"
                      style={{
                        background: typeColors[row.instructor_type]?.bg,
                        color: typeColors[row.instructor_type]?.color,
                      }}
                    >
                      {row.instructor_type}
                    </span>
                  </td>
                  <td>{renderCountry(row.country)}</td>
                  <td>
                    <PopoverSubjects subjects={row.subjects} rowId={row.id} />
                  </td>
                  <td>
                    <Box className="instructor-session-type-icons-style">
                      {row?.allow_one_on_one_session && (
                        <img src={OneOnOneSessionIcon} alt="One-On-One" />
                      )}
                      {row?.allow_one_on_one_session && row?.allow_group_session && (
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: '16px',
                            borderColor: '#D5DEE3',
                          }}
                        />
                      )}
                      {row?.allow_group_session && <img src={GroupSessionIcon} alt="Group" />}
                      {row?.allow_group_session && row?.allow_prime_session && (
                        <Divider
                          orientation="vertical"
                          flexItem
                          sx={{
                            height: '16px',
                            borderColor: '#D5DEE3',
                          }}
                        />
                      )}
                      {row?.allow_prime_session && <img src={PrimeSessionIcon} alt="Prime" />}
                    </Box>
                  </td>
                  <td>{row.total_sessions || 0}</td>
                  <td>{row.rating ? `${row.rating}/5` : '0/5'}</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </Box>
    </Box>
  );
};

export default InstructorTable;
